/*!

=========================================================
* Argon Dashboard React - v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// reactstrap components
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactAplayer from 'react-aplayer';
import { Alert, Container, Row, Spinner } from "reactstrap";
import videojs, { VideoJsPlayer, VideoJsPlayerOptions } from 'video.js';
import "video.js/dist/video-js.css";
import { useExecutionInfo } from "../../hooks/useExecutionInfo";
import { useStoreActions, useStoreState } from "../../stores/store";
import { isVideo } from '../../utils/isVideo';

const ExecutionInfoHeader: React.FC<{
  id: string
}> = ({ id }) => {
  const { data, isLoading, isError } = useExecutionInfo(id)

  const seekMs = useStoreState(state => state.seekToMs)
  const seekTo = useStoreActions(actions => actions.seekTo)
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<any>(null);
  const playerRef = useRef<VideoJsPlayer | null>(null);
  const [fileType, setFileType] = useState<'audio' | 'video'>()

  const token = useMemo(() => localStorage.getItem('token'), [])

  const options: VideoJsPlayerOptions | undefined = useMemo(() => {
    let type = ''
    if (isError || isLoading || !data || !data.videoFileUrl) {
      return;
    }
    const videoUrl = data.videoFileUrl + `?token=${token}`

    if (videoUrl.toLowerCase().endsWith('.mkv')) {
      type = 'video/webm'
    } else {
      type = 'video/mp4'
    }
    return {
      sources: [
        { src: videoUrl, type }
      ],
      fill: true,
      autoplay: true,
      fluid: true,
      preload: 'auto',
      muted: true,
      controls: true,
    }
  }, [data, isLoading, isError])

  useEffect(() => {
    const file = data?.videoFileUrl
    if (!file) {
      return
    }
    if (isVideo(file)) {
      setFileType('video')
    } else {
      setFileType('audio')
    }
  }, [data])

  const handleSeek = useCallback((seconds: number) => {
    if (playerRef.current) {
      playerRef.current.currentTime(seconds)
    } else if (audioRef?.current) {
      const audioElem = audioRef.current
      audioElem.seek(seconds);
    }
    // reset store value so that if user presses seek on same time twice
    // it seeks properly
    seekTo(-1)
  }, [seekTo])

  useEffect(() => {
    if (seekMs >= 0) {
      handleSeek(seekMs / 1000)
    }
  }, [seekMs, handleSeek])

  useEffect(() => {
    if (fileType === "video") {
      if (!playerRef.current) {
        const videoElement = videoRef.current;
        if (!videoElement) return;

        playerRef.current = videojs(videoElement, options, () => {
          console.debug("player is ready");
        });
      }
    }
  }, [options, playerRef, videoRef, fileType]);

  useEffect(() => {
    const player = playerRef?.current;
    return () => {
      if (player) {
        player.dispose();
        playerRef.current = null;
      }
    }
  }, []);

  let content: JSX.Element | null = null
  if (isLoading) {
    content = <Spinner />
  } else if (isError) {
    content = <Alert color="warning">Something went wrong</Alert>
  } else if (data && !data.videoFileUrl) {
    content = <h2>No video/audio available!</h2>
  } else {
    if (fileType === "video") {
      content = <div data-vjs-player>
        <video ref={videoRef} className="video-js vjs-big-play-centered">
          <track kind="captions" src={data?.subtitlesUrl + `?token=${token}`} srcLang="en" label="English" default></track>
        </video>
      </div>
    } else {
      content = <div style={{ position: "relative", width: "100%" }}>
        <ReactAplayer
          theme="#F57F17"
          lrcType={1}
          audio={[
            {
              url: data?.videoFileUrl + `?token=${token}`,
              name: data?.execution.confName.slice(0, 20),
              artist: data?.execution.sessionName.slice(0, 20),
              lrc: data?.lrc || ''
            }
          ]}
          onInit={(player: any) => {
            audioRef.current = player;
          }}
        />
      </div>
    }
  }

  return (
    <>
      <div
        className="header pb-8 pt-5 pt-lg-8 d-flex align-items-center"
        style={{
          minHeight: "500px",
          backgroundColor: '#2D315C',
          backgroundSize: "cover",
          backgroundPosition: "center top",
        }}
      >
        {/* Mask */}
        <span className="mask bg-gradient-default opacity-8" />
        <Container className="d-flex align-items-center" fluid>
          <Row className="video-container mx-auto">
            {content}
          </Row>
        </Container>
      </div>
    </>
  );
};

export default ExecutionInfoHeader;
